import React, { useState, useEffect, useCallback } from "react";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import AppLayout from "@cloudscape-design/components/app-layout";
import SideNavigation from "@cloudscape-design/components/side-navigation";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import Input from "@cloudscape-design/components/input";
import FormField from "@cloudscape-design/components/form-field";
import Alert from "@cloudscape-design/components/alert";
import TextFilter from "@cloudscape-design/components/text-filter";
import Pagination from "@cloudscape-design/components/pagination";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Checkbox from "@cloudscape-design/components/checkbox";
import Modal from "@cloudscape-design/components/modal";
import Link from "@cloudscape-design/components/link";

import { useTranslation } from "react-i18next";
import "./i18n";
import CONFIG from "./config";

function App() {
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [session, setSession] = useState(null);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDemoId, setSelectedDemoId] = useState(() => {
    return localStorage.getItem("selectedDemoId") || null;
  });
  const [selectedUserEmail, setSelectedUserEmail] = useState(() => {
    return localStorage.getItem("selectedUserEmail") || null;
  });
  const [demoSearchText, setDemoSearchText] = useState("");
  const [showTestingDemos, setShowTestingDemos] = useState(() => {
    const saved = localStorage.getItem("showTestingDemos");
    return saved === "true";
  });
  const [timezoneModalVisible, setTimezoneModalVisible] = useState(false);
  const [timezoneModalData, setTimezoneModalData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [demoPageIndex, setDemoPageIndex] = useState(1);
  const [demoPageSize, setDemoPageSize] = useState(10);
  const [userPageIndex, setUserPageIndex] = useState(1);
  const [userPageSize, setUserPageSize] = useState(10);
  const [allowlistData, setAllowlistData] = useState(null);
  const [loadingAllowlist, setLoadingAllowlist] = useState(false);
  const [newDemoId, setNewDemoId] = useState("");
  const [newDemoDescription, setNewDemoDescription] = useState("");
  const [allowlistError, setAllowlistError] = useState("");
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("activeView") || "usage";
  });
  const [navWidth, setNavWidth] = useState(() => Math.min(280, Math.max(200, Math.round(window.innerWidth * 0.15))));
  const [loginEventsModalVisible, setLoginEventsModalVisible] = useState(false);
  const [loginEventsData, setLoginEventsData] = useState([]);
  const [loginEventsLoading, setLoginEventsLoading] = useState(false);
  const [loginEventsUser, setLoginEventsUser] = useState(null);
  const [demoToRemove, setDemoToRemove] = useState(null);
  const [editingDemo, setEditingDemo] = useState(null);
  const [editDescription, setEditDescription] = useState("");

  // Get current language name for display
  const getLanguageName = (lng) => {
    const names = { en: "🇬🇧 English", es: "🇪🇸 Español", fr: "🇫🇷 Français", ja: "🇯🇵 日本語", ko: "🇰🇷 한국어", zh: "🇨🇳 中文" };
    return names[lng] || lng;
  };

  const languageOptions = [
    { label: "🇬🇧 English", value: "en" },
    { label: "🇪🇸 Español", value: "es" },
    { label: "🇫🇷 Français", value: "fr" },
    { label: "🇯🇵 日本語", value: "ja" },
    { label: "🇰🇷 한국어", value: "ko" },
    { label: "🇨🇳 中文", value: "zh" },
  ];

  // Helper function to extract Amazon alias from email and create Slack link
  const getSlackLink = (email) => {
    if (!email) return null;
    // Extract username from email (everything before @)
    const username = email.split("@")[0];
    return `https://slack.com/app_redirect?channel=@${username}`;
  };

  // Responsive nav width
  useEffect(() => {
    const handleResize = () => setNavWidth(Math.min(280, Math.max(200, Math.round(window.innerWidth * 0.15))));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update current time every second when timezone modal is open
  useEffect(() => {
    if (!timezoneModalVisible) return;

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [timezoneModalVisible]);

  // Save selected demo ID to localStorage
  useEffect(() => {
    if (selectedDemoId) {
      localStorage.setItem("selectedDemoId", selectedDemoId);
    } else {
      localStorage.removeItem("selectedDemoId");
    }
  }, [selectedDemoId]);

  // Save selected user email to localStorage
  useEffect(() => {
    if (selectedUserEmail) {
      localStorage.setItem("selectedUserEmail", selectedUserEmail);
    } else {
      localStorage.removeItem("selectedUserEmail");
    }
  }, [selectedUserEmail]);

  // Save show testing demos preference to localStorage
  useEffect(() => {
    localStorage.setItem("showTestingDemos", showTestingDemos.toString());
  }, [showTestingDemos]);

  // Save active view to localStorage
  useEffect(() => {
    localStorage.setItem("activeView", activeView);
  }, [activeView]);

  // Handle timezone click
  const handleTimezoneClick = (timezone, location) => {
    setTimezoneModalData({ timezone, location });
    setTimezoneModalVisible(true);
  };

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setIdToken(null);
    setUsageData(null);
    localStorage.removeItem("idToken");
    localStorage.removeItem("currentUser");
  }, []);

  const loadUsageData = useCallback(
    async (token) => {
      setLoading(true);
      try {
        const response = await fetch(`${CONFIG.apiUrl}usage`, {
          headers: {
            Authorization: token,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
        } else if (response.status === 401 || response.status === 403) {
          // Token expired or invalid - logout and redirect to login
          console.log("Authentication failed, logging out");
          handleLogout();
        } else {
          console.error("Failed to load usage data");
        }
      } catch (error) {
        console.error("Error loading usage data:", error);
        // Network errors can indicate auth issues (CORS blocks 401/403)
        // Logout to be safe
        handleLogout();
      } finally {
        setLoading(false);
      }
    },
    [handleLogout],
  );

  const loadAllowlistData = useCallback(
    async (token) => {
      setLoadingAllowlist(true);
      try {
        const response = await fetch(`${CONFIG.apiUrl}allowlist`, {
          headers: {
            Authorization: token,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAllowlistData(data);
        } else if (response.status === 401 || response.status === 403) {
          console.log("Authentication failed, logging out");
          handleLogout();
        } else {
          console.error("Failed to load allowlist data");
        }
      } catch (error) {
        console.error("Error loading allowlist data:", error);
        handleLogout();
      } finally {
        setLoadingAllowlist(false);
      }
    },
    [handleLogout],
  );

  const fetchLoginEvents = useCallback(
    async (demoId, userEmail) => {
      setLoginEventsLoading(true);
      setLoginEventsUser({ demoId, userEmail });
      setLoginEventsModalVisible(true);
      try {
        const response = await fetch(
          `${CONFIG.apiUrl}usage?demo_id=${encodeURIComponent(demoId)}&user_email=${encodeURIComponent(userEmail)}&event_type=login`,
          { headers: { Authorization: idToken } },
        );
        if (response.ok) {
          const data = await response.json();
          setLoginEventsData(data.events || []);
        } else {
          setLoginEventsData([]);
        }
      } catch (error) {
        console.error("Error fetching login events:", error);
        setLoginEventsData([]);
      } finally {
        setLoginEventsLoading(false);
      }
    },
    [idToken],
  );

  const handleOAuthCallback = useCallback(
    async (code) => {
      setErrorMessage("");

      try {
        // Construct redirect_uri to match what was sent in the authorization request
        const redirectUri = window.location.origin;

        console.log("Token exchange redirect_uri:", redirectUri);
        console.log("Authorization code:", code);

        const requestBody = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: CONFIG.userPoolClientId,
          code: code,
          redirect_uri: redirectUri,
        });

        console.log("Token request body:", requestBody.toString());

        const response = await fetch(`https://${CONFIG.cognitoDomain}.auth.${CONFIG.region}.amazoncognito.com/oauth2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: requestBody,
        });

        const tokens = await response.json();

        console.log("Token response:", tokens);

        if (tokens.id_token) {
          // Decode token to get email
          const payload = JSON.parse(atob(tokens.id_token.split(".")[1]));
          const token = tokens.id_token;
          let user = payload.email || payload["cognito:username"] || "User";

          // Strip "AmazonFederate_" prefix from Midway usernames
          if (user.startsWith("AmazonFederate_")) {
            user = user.substring("AmazonFederate_".length);
          }

          // Store tokens in localStorage for persistence
          setIdToken(token);
          setCurrentUser(user);
          setIsAuthenticated(true);
          localStorage.setItem("idToken", token);
          localStorage.setItem("currentUser", user);
          localStorage.setItem("loginMechanism", "midway");

          if (tokens.access_token) {
            localStorage.setItem("accessToken", tokens.access_token);
          }
          if (tokens.refresh_token) {
            localStorage.setItem("refreshToken", tokens.refresh_token);
          }

          // Load usage data
          loadUsageData(token);
        } else {
          throw new Error(tokens.error_description || tokens.error || "OAuth token exchange failed");
        }
      } catch (error) {
        console.error("OAuth callback error:", error);
        setErrorMessage(`Login failed: ${error.message}`);
      }
    },
    [loadUsageData],
  );

  useEffect(() => {
    applyMode(Mode.Light);

    // Check if we're handling OAuth callback
    const handleOAuthFlow = async () => {
      if (CONFIG.midwayEnabled) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");

        if (code) {
          // Check if we have a stored flag indicating we initiated this OAuth flow
          const oauthInProgress = sessionStorage.getItem("oauthInProgress");

          if (oauthInProgress) {
            // Clear the flag and the code from URL immediately to prevent reuse
            sessionStorage.removeItem("oauthInProgress");
            window.history.replaceState({}, document.title, window.location.pathname);
            await handleOAuthCallback(code);
            return true;
          } else {
            // Code in URL but we didn't initiate OAuth - probably from browser history
            // Clear it and continue with normal flow
            console.log("Stale OAuth code detected, clearing...");
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
      return false;
    };

    // Run OAuth flow check first
    handleOAuthFlow().then((handledOAuth) => {
      if (handledOAuth) {
        return; // OAuth callback handled, don't check for existing session
      }

      // Check for existing session
      const token = localStorage.getItem("idToken");
      let user = localStorage.getItem("currentUser");

      if (token && user) {
        // Strip "AmazonFederate_" prefix if present (for old stored values)
        if (user.startsWith("AmazonFederate_")) {
          user = user.substring("AmazonFederate_".length);
          localStorage.setItem("currentUser", user); // Update stored value
        }

        setIdToken(token);
        setCurrentUser(user);
        setIsAuthenticated(true);
        loadUsageData(token);
        loadAllowlistData(token);
      }
    });
  }, [handleOAuthCallback, loadUsageData, loadAllowlistData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const response = await fetch(`https://cognito-idp.${CONFIG.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
        },
        body: JSON.stringify({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: CONFIG.userPoolClientId,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        }),
      });

      const data = await response.json();

      if (data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        // User needs to change password
        setSession(data.Session);
        setNeedsPasswordChange(true);
      } else if (data.AuthenticationResult) {
        const token = data.AuthenticationResult.IdToken;
        setIdToken(token);
        setCurrentUser(email);
        setIsAuthenticated(true);
        localStorage.setItem("idToken", token);
        localStorage.setItem("currentUser", email);
        loadUsageData(token);
      } else if (data.__type) {
        setErrorMessage(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login failed:", error);
      setErrorMessage("Login failed. Please check your credentials.");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    try {
      const response = await fetch(`https://cognito-idp.${CONFIG.region}.amazonaws.com/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-amz-json-1.1",
          "X-Amz-Target": "AWSCognitoIdentityProviderService.RespondToAuthChallenge",
        },
        body: JSON.stringify({
          ChallengeName: "NEW_PASSWORD_REQUIRED",
          ClientId: CONFIG.userPoolClientId,
          ChallengeResponses: {
            USERNAME: email,
            PASSWORD: password,
            NEW_PASSWORD: newPassword,
          },
          Session: session,
        }),
      });

      const data = await response.json();

      if (data.AuthenticationResult) {
        const token = data.AuthenticationResult.IdToken;
        setIdToken(token);
        setCurrentUser(email);
        setIsAuthenticated(true);
        setNeedsPasswordChange(false);
        localStorage.setItem("idToken", token);
        localStorage.setItem("currentUser", email);
        loadUsageData(token);
      } else if (data.__type) {
        setErrorMessage(data.message || "Password change failed");
      }
    } catch (error) {
      console.error("Password change failed:", error);
      setErrorMessage("Password change failed. Please try again.");
    }
  };

  const handleMidwayLogin = () => {
    const redirectUri = window.location.origin;
    const cognitoUrl = `https://${CONFIG.cognitoDomain}.auth.${CONFIG.region}.amazoncognito.com`;

    // Set flag to indicate we're starting OAuth flow
    sessionStorage.setItem("oauthInProgress", "true");

    const authUrl = `${cognitoUrl}/oauth2/authorize?identity_provider=AmazonFederate&redirect_uri=${redirectUri}&response_type=code&client_id=${CONFIG.userPoolClientId}&scope=openid`;
    window.location.href = authUrl;
  };

  const handleAddDemo = async () => {
    setAllowlistError("");

    if (!newDemoId.trim()) {
      setAllowlistError("Demo ID is required");
      return;
    }

    try {
      const response = await fetch(`${CONFIG.apiUrl}allowlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          demo_id: newDemoId.trim(),
          description: newDemoDescription.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Success - reload allowlist and clear form
        setNewDemoId("");
        setNewDemoDescription("");
        loadAllowlistData(idToken);
      } else {
        setAllowlistError(data.error || "Failed to add demo");
      }
    } catch (error) {
      console.error("Error adding demo:", error);
      setAllowlistError("Failed to add demo");
    }
  };

  const handleRemoveDemo = async (demoId) => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}allowlist/${demoId}`, {
        method: "DELETE",
        headers: {
          Authorization: idToken,
        },
      });

      if (response.ok) {
        loadAllowlistData(idToken);
      } else {
        const data = await response.json();
        console.error("Failed to remove demo:", data.error);
      }
    } catch (error) {
      console.error("Error removing demo:", error);
    }
    setDemoToRemove(null);
  };

  const handleEditDemo = (item) => {
    setEditingDemo(item);
    setEditDescription(item.description || "");
  };

  const handleSaveEdit = async () => {
    if (!editingDemo) return;
    try {
      const response = await fetch(`${CONFIG.apiUrl}allowlist/${editingDemo.demo_id}`, {
        method: "PUT",
        headers: {
          Authorization: idToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ description: editDescription }),
      });

      if (response.ok) {
        loadAllowlistData(idToken);
      } else {
        const data = await response.json();
        console.error("Failed to update demo:", data.error);
      }
    } catch (error) {
      console.error("Error updating demo:", error);
    }
    setEditingDemo(null);
  };

  if (!isAuthenticated) {
    if (needsPasswordChange) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: "#f9f9f9" }}>
          <Container header={<Header variant="h1">{t("auth.password.changeTitle")}</Header>}>
            <form onSubmit={handlePasswordChange}>
              <SpaceBetween size="l">
                {errorMessage && (
                  <Alert type="error" dismissible onDismiss={() => setErrorMessage("")}>
                    {errorMessage}
                  </Alert>
                )}

                <FormField label={t("auth.password.new")} description="Must be at least 8 characters with uppercase, lowercase, numbers, and symbols">
                  <Input value={newPassword} onChange={({ detail }) => setNewPassword(detail.value)} placeholder="Enter new password" type="password" />
                </FormField>

                <FormField label={t("auth.password.confirm")}>
                  <Input
                    value={confirmPassword}
                    onChange={({ detail }) => setConfirmPassword(detail.value)}
                    placeholder="Confirm new password"
                    type="password"
                  />
                </FormField>

                <Button variant="primary" formAction="submit" fullWidth>
                  {t("auth.password.change")}
                </Button>
              </SpaceBetween>
            </form>
          </Container>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: "#f9f9f9" }}>
        <Container header={<Header variant="h1">{t("app.common.usageTrackingAdmin")}</Header>}>
          <form onSubmit={handleLogin}>
            <SpaceBetween size="l">
              {errorMessage && (
                <Alert type="error" dismissible onDismiss={() => setErrorMessage("")}>
                  {errorMessage}
                </Alert>
              )}

              <FormField label={t("auth.login.email")}>
                <Input value={email} onChange={({ detail }) => setEmail(detail.value)} placeholder="Enter your email" type="email" />
              </FormField>

              <FormField label={t("auth.login.password")}>
                <Input value={password} onChange={({ detail }) => setPassword(detail.value)} placeholder="Enter your password" type="password" />
              </FormField>

              <SpaceBetween size="s">
                <Button variant="primary" formAction="submit" fullWidth>
                  {t("auth.login.button")}
                </Button>

                {CONFIG.midwayEnabled && (
                  <>
                    <Box textAlign="center">OR</Box>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        handleMidwayLogin();
                      }}
                      fullWidth
                    >
                      {t("auth.midway.signIn")}
                    </Button>
                  </>
                )}
              </SpaceBetween>
            </SpaceBetween>
          </form>
        </Container>
      </div>
    );
  }

  return (
    <>
      <TopNavigation
        identity={{
          href: "/",
          title: t("app.common.usageTrackingAdmin"),
        }}
        utilities={[
          {
            type: "menu-dropdown",
            text: getLanguageName(i18n.language),
            items: languageOptions.map((lang) => ({
              id: lang.value,
              text: lang.label,
            })),
            onItemClick: ({ detail }) => {
              i18n.changeLanguage(detail.id);
            },
          },
          {
            type: "button",
            text: currentUser,
            description: currentUser,
            iconName: "user-profile",
          },
          {
            type: "button",
            text: t("auth.common.logout"),
            onClick: handleLogout,
          },
        ]}
      />
      <AppLayout
        navigationWidth={navWidth}
        navigation={
          <SideNavigation
            activeHref={`#/${activeView}`}
            header={{ text: t("app.common.usageTrackingAdmin"), href: "#/" }}
            items={[
              { type: "link", text: t("app.navigation.usage"), href: "#/usage" },
              { type: "link", text: t("app.navigation.allowlist"), href: "#/allowlist" },
            ]}
            onFollow={(event) => {
              event.preventDefault();
              const view = event.detail.href.replace("#/", "");
              setActiveView(view || "usage");
            }}
          />
        }
        toolsHide
        content={
          activeView === "usage" ? (
            <SpaceBetween size="l">
              <Container
                header={
                  <Header
                    variant="h1"
                    actions={
                      <Button iconName="refresh" onClick={() => loadUsageData(idToken)} loading={loading}>
                        {t("buttons.common.refresh")}
                      </Button>
                    }
                  >
                    {t("app.common.title")}
                  </Header>
                }
              >
                {usageData && usageData.demos_summary && (
                  <SpaceBetween size="m">
                    <Checkbox checked={showTestingDemos} onChange={({ detail }) => setShowTestingDemos(detail.checked)}>
                      {t("app.common.showTestingData")}
                    </Checkbox>
                    <Table
                      columnDefinitions={[
                        {
                          id: "demo_name",
                          header: t("table.columns.demoName"),
                          cell: (item) => item.demo_description || item.demo_id,
                          sortingComparator: (a, b) => {
                            return (a.demo_description || a.demo_id).toLowerCase().localeCompare((b.demo_description || b.demo_id).toLowerCase());
                          },
                        },
                        {
                          id: "hostname",
                          header: t("table.columns.hostname"),
                          cell: (item) => item.demo_hostname || "-",
                          sortingField: "demo_hostname",
                        },
                        {
                          id: "users",
                          header: t("table.columns.totalUsers"),
                          cell: (item) => item.total_users,
                          sortingField: "total_users",
                        },
                        {
                          id: "logins",
                          header: t("table.columns.totalLogins"),
                          cell: (item) => item.total_logins,
                          sortingField: "total_logins",
                        },
                        {
                          id: "opportunities",
                          header: t("table.columns.opportunities"),
                          cell: (item) => item.total_opportunities,
                          sortingField: "total_opportunities",
                        },
                      ]}
                      items={(() => {
                        const filtered = usageData.demos_summary.filter((demo) => {
                          if (!showTestingDemos && demo.demo_id === "demo-tracker-test-e8f2a9c4") {
                            return false;
                          }
                          if (!demoSearchText) return true;
                          const searchLower = demoSearchText.toLowerCase();
                          const desc = demo.demo_description || "";
                          return (
                            demo.demo_id.toLowerCase().includes(searchLower) ||
                            (demo.demo_hostname && demo.demo_hostname.toLowerCase().includes(searchLower)) ||
                            desc.toLowerCase().includes(searchLower)
                          );
                        });
                        const start = (demoPageIndex - 1) * demoPageSize;
                        return filtered.slice(start, start + demoPageSize);
                      })()}
                      loading={loading}
                      sortingDisabled={false}
                      selectionType="single"
                      selectedItems={selectedDemoId ? usageData.demos_summary.filter((d) => d.demo_id === selectedDemoId) : []}
                      onSelectionChange={({ detail }) => {
                        if (detail.selectedItems.length > 0) {
                          setSelectedDemoId(detail.selectedItems[0].demo_id);
                          setSelectedUserEmail(null);
                          setUserPageIndex(1);
                        } else {
                          setSelectedDemoId(null);
                          setSelectedUserEmail(null);
                        }
                      }}
                      onRowClick={({ detail }) => {
                        setSelectedDemoId(detail.item.demo_id);
                        setSelectedUserEmail(null);
                        setUserPageIndex(1);
                      }}
                      filter={
                        <TextFilter
                          filteringText={demoSearchText}
                          filteringPlaceholder={t("app.common.searchPlaceholder")}
                          onChange={({ detail }) => {
                            setDemoSearchText(detail.filteringText);
                            setDemoPageIndex(1);
                          }}
                        />
                      }
                      pagination={
                        <Pagination
                          currentPageIndex={demoPageIndex}
                          pagesCount={
                            Math.ceil(
                              usageData.demos_summary.filter((demo) => {
                                if (!showTestingDemos && demo.demo_id === "demo-tracker-test-e8f2a9c4") return false;
                                if (!demoSearchText) return true;
                                const searchLower = demoSearchText.toLowerCase();
                                const desc = demo.demo_description || "";
                                return (
                                  demo.demo_id.toLowerCase().includes(searchLower) ||
                                  (demo.demo_hostname && demo.demo_hostname.toLowerCase().includes(searchLower)) ||
                                  desc.toLowerCase().includes(searchLower)
                                );
                              }).length / demoPageSize,
                            ) || 1
                          }
                          onChange={({ detail }) => setDemoPageIndex(detail.currentPageIndex)}
                        />
                      }
                      preferences={
                        <CollectionPreferences
                          title="Preferences"
                          confirmLabel="Confirm"
                          cancelLabel="Cancel"
                          preferences={{
                            pageSize: demoPageSize,
                          }}
                          pageSizePreference={{
                            title: "Page size",
                            options: [
                              { value: 10, label: "10 demos" },
                              { value: 25, label: "25 demos" },
                              { value: 50, label: "50 demos" },
                              { value: 100, label: "100 demos" },
                            ],
                          }}
                          onConfirm={({ detail }) => {
                            setDemoPageSize(detail.pageSize);
                            setDemoPageIndex(1);
                          }}
                        />
                      }
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>{t("table.empty.noDemos")}</b>
                        </Box>
                      }
                    />
                  </SpaceBetween>
                )}
              </Container>

              {selectedDemoId && (
                <Container
                  header={
                    <Header variant="h2">
                      {t("admin.headers.usersForDemo")}{" "}
                      {(() => {
                        const demo = usageData && usageData.demos_summary ? usageData.demos_summary.find((d) => d.demo_id === selectedDemoId) : null;
                        return (demo && demo.demo_description) || (demo ? demo.demo_id : "");
                      })()}
                    </Header>
                  }
                >
                  {usageData && usageData.records && (
                    <Table
                      columnDefinitions={[
                        {
                          id: "user_email",
                          header: t("table.columns.userEmail"),
                          cell: (item) => item.user_email,
                          sortingField: "user_email",
                        },
                        {
                          id: "login_count",
                          header: t("table.columns.totalLogins"),
                          cell: (item) => (
                            <Link onFollow={() => fetchLoginEvents(item.demo_id || selectedDemoId, item.user_email)}>{item.login_count || 0}</Link>
                          ),
                          sortingField: "login_count",
                        },
                        {
                          id: "last_login",
                          header: t("table.columns.lastLogin"),
                          cell: (item) => (item.last_login ? new Date(item.last_login).toLocaleString() : t("table.values.never")),
                          sortingField: "last_login",
                        },
                        {
                          id: "opportunities",
                          header: t("table.columns.opportunities"),
                          cell: (item) => (item.opportunities ? item.opportunities.length : 0),
                        },
                        {
                          id: "login_mechanism",
                          header: t("table.columns.loginMechanism"),
                          cell: (item) => item.login_mechanism || "-",
                        },
                      ]}
                      items={(() => {
                        const filtered = usageData.records.filter((record) => record.demo_id === selectedDemoId);
                        const start = (userPageIndex - 1) * userPageSize;
                        return filtered.slice(start, start + userPageSize);
                      })()}
                      loading={loading}
                      sortingDisabled={false}
                      selectionType="single"
                      selectedItems={
                        selectedUserEmail ? usageData.records.filter((r) => r.demo_id === selectedDemoId && r.user_email === selectedUserEmail) : []
                      }
                      onSelectionChange={({ detail }) => {
                        if (detail.selectedItems.length > 0) {
                          setSelectedUserEmail(detail.selectedItems[0].user_email);
                        } else {
                          setSelectedUserEmail(null);
                        }
                      }}
                      onRowClick={({ detail }) => {
                        setSelectedUserEmail(detail.item.user_email);
                      }}
                      pagination={
                        <Pagination
                          currentPageIndex={userPageIndex}
                          pagesCount={Math.ceil(usageData.records.filter((record) => record.demo_id === selectedDemoId).length / userPageSize) || 1}
                          onChange={({ detail }) => setUserPageIndex(detail.currentPageIndex)}
                        />
                      }
                      preferences={
                        <CollectionPreferences
                          title="Preferences"
                          confirmLabel="Confirm"
                          cancelLabel="Cancel"
                          preferences={{
                            pageSize: userPageSize,
                          }}
                          pageSizePreference={{
                            title: "Page size",
                            options: [
                              { value: 10, label: "10 users" },
                              { value: 25, label: "25 users" },
                              { value: 50, label: "50 users" },
                            ],
                          }}
                          onConfirm={({ detail }) => {
                            setUserPageSize(detail.pageSize);
                            setUserPageIndex(1);
                          }}
                        />
                      }
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>{t("table.empty.noUsers")}</b>
                        </Box>
                      }
                    />
                  )}
                </Container>
              )}

              {selectedDemoId && selectedUserEmail && (
                <Container
                  header={
                    <Header variant="h2">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                        <span>
                          {t("admin.headers.userDetails")} {selectedUserEmail}
                        </span>
                        <span style={{ display: "inline-block", marginBottom: "-4px" }}>
                          <Link href={`mailto:${selectedUserEmail}?subject=Demo used: ${selectedDemoId}`} external={false}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                              <img src="/email-16.png" alt="Email" style={{ width: "16px", height: "16px" }} />
                              <span>Email</span>
                            </span>
                          </Link>
                        </span>
                        {getSlackLink(selectedUserEmail) && (
                          <span style={{ display: "inline-block", marginBottom: "-4px" }}>
                            <Link href={getSlackLink(selectedUserEmail)} external={false} target="_blank">
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                <img src="/slack-16.png" alt="Slack" style={{ width: "16px", height: "16px" }} />
                                <span>Slack</span>
                              </span>
                            </Link>
                          </span>
                        )}
                      </span>
                    </Header>
                  }
                >
                  {usageData && usageData.records && (
                    <SpaceBetween size="m">
                      {usageData.records
                        .filter((record) => record.demo_id === selectedDemoId && record.user_email === selectedUserEmail)
                        .map((record) => (
                          <SpaceBetween size="l" key={`${record.demo_id}-${record.user_email}`}>
                            {/* User Summary Info in Multi-Column Layout */}
                            <Container>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.demoName")}</Box>
                                  <div>
                                    {(() => {
                                      const demo = usageData.demos_summary ? usageData.demos_summary.find((d) => d.demo_id === record.demo_id) : null;
                                      return (demo && demo.demo_description) || record.demo_id;
                                    })()}
                                  </div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.demoId")}</Box>
                                  <div>
                                    <Link href={`https://demohub.sa.aws.dev/demos/${record.demo_id}`} external>
                                      {record.demo_id}
                                    </Link>
                                  </div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.hostname")}</Box>
                                  <div>{record.demo_hostname || "-"}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.totalLogins")}</Box>
                                  <div>{record.login_count || 0}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.lastLogin")}</Box>
                                  <div>{record.last_login ? new Date(record.last_login).toLocaleString() : t("table.values.never")}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.userLanguage")}</Box>
                                  <div>{record.demo_language || "-"}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.browserMode")}</Box>
                                  <div>{record.browser_mode || "-"}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("table.columns.loginMechanism")}</Box>
                                  <div>{record.login_mechanism || "-"}</div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">{t("admin.user-details.frontend-version")}</Box>
                                  <div>{record.frontend_version || "-"}</div>
                                </div>
                                {record.geolocation && (
                                  <>
                                    {record.geolocation.city && (
                                      <div>
                                        <Box variant="awsui-key-label">{t("table.columns.location")}</Box>
                                        <div>
                                          {record.geolocation.city}
                                          {record.geolocation.region_name && `, ${record.geolocation.region_name}`}
                                          {record.geolocation.country_name && `, ${record.geolocation.country_name}`}
                                        </div>
                                      </div>
                                    )}
                                    {!record.geolocation.city && record.geolocation.country_name && (
                                      <div>
                                        <Box variant="awsui-key-label">{t("table.columns.country")}</Box>
                                        <div>{record.geolocation.country_name}</div>
                                      </div>
                                    )}
                                    {record.geolocation.timezone && (
                                      <div>
                                        <Box variant="awsui-key-label">{t("table.columns.timezone")}</Box>
                                        <div>
                                          <Link
                                            onFollow={() =>
                                              handleTimezoneClick(
                                                record.geolocation.timezone,
                                                `${record.geolocation.city || ""}${
                                                  record.geolocation.city && record.geolocation.country_name ? ", " : ""
                                                }${record.geolocation.country_name || ""}`,
                                              )
                                            }
                                          >
                                            {record.geolocation.timezone}
                                          </Link>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </Container>

                            {/* Opportunities Table */}
                            {record.opportunities && record.opportunities.length > 0 && (
                              <Container
                                header={
                                  <Header variant="h3">
                                    {t("table.columns.opportunities")} ({record.opportunities.length})
                                  </Header>
                                }
                              >
                                <Table
                                  columnDefinitions={[
                                    {
                                      id: "opportunity_id",
                                      header: t("table.columns.opportunityId"),
                                      cell: (opp) =>
                                        opp.opportunity_id ? (
                                          <a
                                            href={`https://aws-crm.lightning.force.com/lightning/r/Opportunity/${opp.opportunity_id}/view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {opp.opportunity_id}
                                          </a>
                                        ) : (
                                          "-"
                                        ),
                                    },
                                    {
                                      id: "customer_name",
                                      header: t("table.columns.customerName"),
                                      cell: (opp) => opp.customer_name || "-",
                                    },
                                    {
                                      id: "demo_language",
                                      header: t("table.columns.language"),
                                      cell: (opp) => opp.demo_language || "-",
                                    },
                                    {
                                      id: "browser_mode",
                                      header: t("table.columns.mode"),
                                      cell: (opp) => opp.browser_mode || "-",
                                    },
                                    {
                                      id: "notes",
                                      header: t("table.columns.notes"),
                                      cell: (opp) => opp.notes || "-",
                                    },
                                    {
                                      id: "logged_at",
                                      header: t("table.columns.loggedAt"),
                                      cell: (opp) => (opp.logged_at ? new Date(opp.logged_at).toLocaleString() : "-"),
                                    },
                                  ]}
                                  items={record.opportunities}
                                  variant="embedded"
                                  empty={
                                    <Box textAlign="center" color="inherit">
                                      <b>{t("table.empty.noOpportunities")}</b>
                                    </Box>
                                  }
                                />
                              </Container>
                            )}
                          </SpaceBetween>
                        ))}
                    </SpaceBetween>
                  )}
                </Container>
              )}
            </SpaceBetween>
          ) : (
            /* Allowlist View */
            <SpaceBetween size="l">
              <Container
                header={
                  <Header
                    variant="h1"
                    description={t("admin.allowlist.description")}
                    actions={
                      <Button iconName="refresh" onClick={() => loadAllowlistData(idToken)} loading={loadingAllowlist}>
                        {t("buttons.common.refresh")}
                      </Button>
                    }
                  >
                    {t("admin.allowlist.title")}
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  {/* Add Demo Form */}
                  <Container>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAddDemo();
                      }}
                    >
                      <SpaceBetween size="m">
                        {allowlistError && (
                          <Alert type="error" dismissible onDismiss={() => setAllowlistError("")}>
                            {allowlistError}
                          </Alert>
                        )}
                        <FormField label={t("admin.allowlist.demoId")}>
                          <Input value={newDemoId} onChange={({ detail }) => setNewDemoId(detail.value)} placeholder={t("admin.allowlist.demoIdPlaceholder")} />
                        </FormField>
                        <FormField label={t("admin.allowlist.demoDescription")}>
                          <Input
                            value={newDemoDescription}
                            onChange={({ detail }) => setNewDemoDescription(detail.value)}
                            placeholder={t("admin.allowlist.demoDescriptionPlaceholder")}
                          />
                        </FormField>
                        <Button variant="primary" formAction="submit">
                          {t("admin.allowlist.addDemo")}
                        </Button>
                      </SpaceBetween>
                    </form>
                  </Container>

                  {/* Allowlist Table */}
                  {allowlistData && (
                    <Table
                      columnDefinitions={[
                        {
                          id: "demo_id",
                          header: t("admin.allowlist.demoId"),
                          cell: (item) => item.demo_id,
                          sortingField: "demo_id",
                        },
                        {
                          id: "description",
                          header: t("admin.allowlist.demoDescription"),
                          cell: (item) => item.description || "-",
                        },
                        {
                          id: "status",
                          header: t("admin.allowlist.status"),
                          cell: (item) => (item.status === "active" ? t("admin.allowlist.active") : t("admin.allowlist.inactive")),
                        },
                        {
                          id: "created_by",
                          header: t("admin.allowlist.createdBy"),
                          cell: (item) => item.created_by || "-",
                        },
                        {
                          id: "created_at",
                          header: t("admin.allowlist.createdAt"),
                          cell: (item) => (item.created_at ? new Date(item.created_at).toLocaleString() : "-"),
                        },
                        {
                          id: "actions",
                          header: t("admin.allowlist.actions"),
                          cell: (item) =>
                            item.status === "active" ? (
                              <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="edit" variant="icon" ariaLabel={t("admin.allowlist.edit")} onClick={() => handleEditDemo(item)} />
                                <Button
                                  iconName="remove"
                                  variant="icon"
                                  ariaLabel={t("admin.allowlist.remove")}
                                  onClick={() => setDemoToRemove(item.demo_id)}
                                />
                              </SpaceBetween>
                            ) : (
                              "-"
                            ),
                        },
                      ]}
                      items={allowlistData.demos || []}
                      loading={loadingAllowlist}
                      sortingDisabled={false}
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>{t("admin.allowlist.noDemos")}</b>
                        </Box>
                      }
                    />
                  )}
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          )
        }
        contentFooter={
          <Box textAlign="center" color="text-body-secondary" fontSize="body-s" padding={{ vertical: "s" }}>
            {t("attribution.maxmind")}{" "}
            <a href="https://www.maxmind.com" target="_blank" rel="noopener noreferrer">
              https://www.maxmind.com
            </a>
          </Box>
        }
      />

      {/* Login Events Modal */}
      <Modal
        visible={loginEventsModalVisible}
        onDismiss={() => setLoginEventsModalVisible(false)}
        header={`${t("login-events.modal.title")} — ${loginEventsUser?.userEmail || ""}`}
        size="max"
      >
        <Table
          columnDefinitions={[
            {
              id: "timestamp",
              header: t("login-events.columns.timestamp"),
              cell: (item) => (item.timestamp ? new Date(item.timestamp).toLocaleString() : "-"),
              sortingField: "timestamp",
            },
            {
              id: "demo_hostname",
              header: t("table.columns.hostname"),
              cell: (item) => item.demo_hostname || "-",
            },
            {
              id: "demo_language",
              header: t("table.columns.language"),
              cell: (item) => item.demo_language || "-",
            },
            {
              id: "browser_mode",
              header: t("table.columns.browserMode"),
              cell: (item) => item.browser_mode || "-",
            },
            {
              id: "login_mechanism",
              header: t("table.columns.loginMechanism"),
              cell: (item) => item.login_mechanism || "-",
            },
            {
              id: "location",
              header: t("table.columns.location"),
              cell: (item) => {
                const geo = item.geolocation;
                if (!geo) return "-";
                const parts = [geo.city, geo.region_name, geo.country_name].filter(Boolean);
                return parts.length > 0 ? parts.join(", ") : geo.country || "-";
              },
            },
            {
              id: "timezone",
              header: t("table.columns.timezone"),
              cell: (item) => (item.geolocation && item.geolocation.timezone ? item.geolocation.timezone : "-"),
            },
          ]}
          items={loginEventsData}
          loading={loginEventsLoading}
          loadingText={t("login-events.modal.loading")}
          sortingDisabled={false}
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t("login-events.modal.noEvents")}</b>
            </Box>
          }
        />
      </Modal>

      {/* Timezone Modal */}
      <Modal visible={timezoneModalVisible} onDismiss={() => setTimezoneModalVisible(false)} header={t("timezone-modal.title")} size="medium">
        {timezoneModalData && (
          <SpaceBetween size="l">
            <div>
              <Box variant="awsui-key-label">
                {t("timezone-modal.current-time-in")} {timezoneModalData.location || t("timezone-modal.unknown")}
              </Box>
              <div style={{ marginTop: "8px" }}>
                {currentTime.toLocaleString("en-US", {
                  timeZone: timezoneModalData.timezone,
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>
            <div>
              <Box variant="awsui-key-label">{t("timezone-modal.your-time")}</Box>
              <div style={{ marginTop: "8px" }}>
                {currentTime.toLocaleString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>
          </SpaceBetween>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={!!demoToRemove}
        onDismiss={() => setDemoToRemove(null)}
        header={t("admin.allowlist.confirmRemoveTitle")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDemoToRemove(null)}>
                {t("admin.allowlist.cancelButton")}
              </Button>
              <Button variant="primary" onClick={() => handleRemoveDemo(demoToRemove)}>
                {t("admin.allowlist.confirmRemoveButton")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t("admin.allowlist.confirmRemoveMessage", { demoId: demoToRemove })}
      </Modal>

      {/* Edit Demo Modal */}
      <Modal
        visible={!!editingDemo}
        onDismiss={() => setEditingDemo(null)}
        header={t("admin.allowlist.editTitle")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditingDemo(null)}>
                {t("admin.allowlist.cancelButton")}
              </Button>
              <Button variant="primary" onClick={handleSaveEdit}>
                {t("admin.allowlist.editButton")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label={t("admin.allowlist.demoId")}>
            <Input value={editingDemo?.demo_id || ""} disabled />
          </FormField>
          <FormField label={t("admin.allowlist.demoDescription")}>
            <Input value={editDescription} onChange={({ detail }) => setEditDescription(detail.value)} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
}

export default App;
